import { Controller } from '@hotwired/stimulus';

const READING_PAUSE = 5000;
const PIXELS_PER_SECOND = 16;
const PANEL = '.character-list-2__conditions';

// Only the current canonical character scrolls automatically. Inactive icons,
// focused inactive cards and timeline copies remain still and can be read freely.
export default class extends Controller {
  static values = { preview: Boolean };

  connect() {
    this.connected = true;
    this.panels = new Map();
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.motionChanged = () => this.pauseAndSchedule();
    this.visibilityChanged = () => this.pauseAndSchedule();
    this.intersectionObserver = new IntersectionObserver(entries => {
      const now = performance.now();
      for (const entry of entries) {
        const state = this.panels.get(entry.target);
        if (!state) continue;
        if (entry.isIntersecting && !state.intersecting) state.pauseUntil = now + READING_PAUSE;
        state.intersecting = entry.isIntersecting;
        state.startedAt = null;
        if (state.intersecting) this.measure(entry.target, state);
      }
      this.schedule();
    });
    this.resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const state = this.panels.get(entry.target);
        if (state?.intersecting) this.measure(entry.target, state);
      }
      this.schedule();
    });
    this.observer = new MutationObserver(() => this.refresh());
    this.observer.observe(this.element, {
      subtree: true, childList: true, characterData: true,
      attributes: true, attributeFilter: ['class', 'data-character-list-2-positioned']
    });
    this.pointerOver = event => this.pausePanel(event, 'hovered', true);
    this.pointerOut = event => this.pausePanel(event, 'hovered', false);
    this.focusIn = event => this.pausePanel(event, 'focused', true);
    this.focusOut = event => this.pausePanel(event, 'focused', false);
    this.listeners = { pointerover: this.pointerOver, pointerout: this.pointerOut, focusin: this.focusIn, focusout: this.focusOut };
    for (const [name, listener] of Object.entries(this.listeners)) this.element.addEventListener(name, listener);
    this.motion.addEventListener('change', this.motionChanged);
    document.addEventListener('visibilitychange', this.visibilityChanged);
    this.sync();
  }

  get canScroll() {
    return this.connected && !this.previewValue && !this.motion.matches && !document.hidden;
  }

  refresh() {
    if (!this.connected || this.refreshQueued) return;
    this.refreshQueued = true;
    queueMicrotask(() => {
      this.refreshQueued = false;
      if (this.connected) this.sync();
    });
  }

  sync() {
    const current = new Set([...this.element.querySelectorAll(PANEL)].filter(panel =>
      panel.closest('[data-character-list-2-card]')?.classList.contains('is-active')));
    for (const panel of current) {
      let state = this.panels.get(panel);
      if (!state) {
        state = { direction: 1, startedAt: null, pauseUntil: 0, intersecting: false, hovered: false, focused: false };
        this.panels.set(panel, state);
        this.intersectionObserver.observe(panel);
        this.resizeObserver.observe(panel);
      } else if (state.intersecting) {
        this.measure(panel, state);
      }
    }
    for (const panel of this.panels.keys()) {
      if (current.has(panel)) continue;
      this.intersectionObserver.unobserve(panel);
      this.resizeObserver.unobserve(panel);
      this.panels.delete(panel);
    }
    this.schedule();
  }

  measure(panel, state) {
    state.maximum = Math.max(0, panel.scrollHeight - panel.clientHeight);
    state.cssVisible = getComputedStyle(panel).visibility !== 'hidden';
    if (panel.scrollTop > state.maximum) panel.scrollTop = state.maximum;
  }

  pausePanel(event, key, paused) {
    const panel = event.target.closest?.(PANEL);
    const state = this.panels.get(panel);
    if (!state || panel.contains(event.relatedTarget)) return;
    state[key] = paused;
    state.startedAt = null;
    if (!paused) state.pauseUntil = performance.now() + READING_PAUSE;
    this.schedule();
  }

  pauseAndSchedule() {
    for (const state of this.panels.values()) state.startedAt = null;
    this.schedule();
  }

  schedule() {
    this.stop();
    this.tick(performance.now());
  }

  tick(now) {
    this.frame = undefined;
    this.timer = undefined;
    if (!this.canScroll) return;
    let moving = false;
    let wakeAt = Infinity;
    for (const [panel, state] of this.panels) {
      if (!state.intersecting || !state.cssVisible || state.maximum <= 1 || state.hovered || state.focused) continue;
      if (state.pauseUntil > now) {
        wakeAt = Math.min(wakeAt, state.pauseUntil);
        continue;
      }
      if (state.startedAt === null) {
        state.startedAt = now;
        state.start = panel.scrollTop;
      }
      const distance = (now - state.startedAt) * PIXELS_PER_SECOND / 1000;
      const next = Math.max(0, Math.min(state.maximum, state.start + state.direction * distance));
      panel.scrollTop = next;
      if ((state.direction > 0 && next >= state.maximum) || (state.direction < 0 && next <= 0)) {
        state.direction *= -1;
        state.startedAt = null;
        state.pauseUntil = now + READING_PAUSE;
        wakeAt = Math.min(wakeAt, state.pauseUntil);
      } else {
        moving = true;
      }
    }
    if (moving) {
      this.frame = requestAnimationFrame(time => this.tick(time));
    } else if (Number.isFinite(wakeAt)) {
      this.timer = setTimeout(() => this.tick(performance.now()), Math.max(0, wakeAt - now));
    }
  }

  stop() {
    clearTimeout(this.timer);
    cancelAnimationFrame(this.frame);
    this.timer = undefined;
    this.frame = undefined;
  }

  disconnect() {
    this.connected = false;
    this.stop();
    this.observer.disconnect();
    this.intersectionObserver.disconnect();
    this.resizeObserver.disconnect();
    this.panels.clear();
    this.motion.removeEventListener('change', this.motionChanged);
    document.removeEventListener('visibilitychange', this.visibilityChanged);
    for (const [name, listener] of Object.entries(this.listeners)) this.element.removeEventListener(name, listener);
  }
}
